import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  TablePagination,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  GET_CLASS_LIST,
  GET_INSTRUCTOR_LIST,
  GET_CLASS_NAMES,
  GET_MY_PURCHASED_PASSES,
} from "../graphql/query";
import ClassToolBar from "./ClassToolBar";
import themestyle from "../theme";
import { formatTo12Hour } from "../utils/time";
import { CLASS_TYPE_UI } from "../utils/const";
import CenteredLoader from "./CenteredLoader";
import CustomPaginationActions from "./CustomPaginationActions";
import CustomSnackbar from "./Snackbar";
import ClassPurchaseDrawer from "./ClassPurchaseDrawer";
import type { GetMyPurchasedPassesResponse } from "../interfaces/studentPassPurchase.interface";
import { BOOK_CLASS_WITH_PASS } from "../graphql/mutation";
import TruncatedTitle from "./TruncatedTitle";

interface UserClassesCardViewProps {
  onView: (id: string) => void;
  refreshTrigger?: number;
  initialOpenClass?: {
    classId: string;
    sessionDate?: string;
  } | null;
}

const getJoinedClassState = (purchases: GetMyPurchasedPassesResponse["getMyPurchasedPasses"]["data"] | undefined) => {
  const sessionKeys = new Set<string>();

  purchases?.forEach((purchase) => {
    if (purchase?.status !== "ACTIVE") return;
    if (Array.isArray(purchase.bookedSessions) && purchase.bookedSessions.length > 0) {
      purchase.bookedSessions.forEach((session) => {
        if (session?.classId && session?.sessionDate) {
          sessionKeys.add(`${session.classId}|${session.sessionDate}`);
        }
      });
      return;
    }
    const bookedDates = Array.isArray(purchase.bookedSessionDates)
      ? purchase.bookedSessionDates
      : purchase.bookedSessionDate
        ? [purchase.bookedSessionDate]
        : [];
    if (purchase.classId && bookedDates.length > 0) {
      bookedDates.forEach((date) => sessionKeys.add(`${purchase.classId}|${date}`));
    }
  });

  return { sessionKeys };
};

const UserClassesCardView: React.FC<UserClassesCardViewProps> = ({
  onView: _onView,
  refreshTrigger,
  initialOpenClass,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedClassType, setSelectedClassType] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"current" | "upcoming" | "previous">(() => {
    const sessionDate = initialOpenClass?.sessionDate;
    if (!sessionDate) return "current";
    const today = new Date().toISOString().split("T")[0];
    if (sessionDate === today) return "current";
    return sessionDate > today ? "upcoming" : "previous";
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 3 : 9);
  const [selectedClassToBook, setSelectedClassToBook] = useState<any | null>(null);
  const [hasAutoOpenedClass, setHasAutoOpenedClass] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error" | "info">("info");
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setRowsPerPage(isMobile ? 3 : 9);
    setPage(0);
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const schedulePassesRedirect = (state?: Record<string, any>) => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    redirectTimeoutRef.current = setTimeout(() => {
      navigate("/passes", state ? { state } : undefined);
    }, 2200);
  };

    const currentTimeStr = new Date().toTimeString().split(" ")[0].substring(0, 5);
  
    const { data, loading, refetch } = useQuery(GET_CLASS_LIST, {
      variables: {
        page: 1,
        perPage: 100,
        search: searchQuery || "",
        instructorName: selectedInstructor,
        day: selectedDay,
        classType: selectedClassType,
        timeline: activeTab.toUpperCase(),
        today: new Date().toISOString().split("T")[0],
        currentTime: currentTimeStr,
        includeCancelled: activeTab === "previous",
      },
      fetchPolicy: "network-only",
    });

  const { data: allClassesData, loading: classOptionsLoading, refetch: refetchAllClasses } = useQuery(GET_CLASS_NAMES, {
    variables: {
      page: 1,
      perPage: 1000,
    },
    fetchPolicy: "network-only",
  });

  const { data: instructorsData, refetch: refetchInstructors } = useQuery(GET_INSTRUCTOR_LIST, {
    variables: { page: 1, perPage: 1000 },
  });

  const { data: myPassesData, refetch: refetchMyPasses } =
    useQuery<GetMyPurchasedPassesResponse>(GET_MY_PURCHASED_PASSES, {
      fetchPolicy: "network-only",
    });
  const [bookClassWithPass] = useMutation(BOOK_CLASS_WITH_PASS);

  useEffect(() => {
    if (refreshTrigger) {
      refetch();
      refetchAllClasses();
      refetchInstructors();
      refetchMyPasses();
    }
  }, [refreshTrigger, refetch, refetchAllClasses, refetchInstructors, refetchMyPasses]);

  const classOptions = useMemo(() => {
    if (!allClassesData?.getClassList?.data) return [];
    const uniqueNames = new Map<string, string>();
    allClassesData.getClassList.data.forEach((c: any) => {
      const lower = c.className.toLowerCase();
      if (!uniqueNames.has(lower)) {
        uniqueNames.set(lower, c.className);
      }
    });
    return Array.from(uniqueNames.values());
  }, [allClassesData]);

  const instructorOptions = instructorsData?.getInstructorList?.data || [];

  // Only sessions explicitly booked by date should show as joined.
  const joinedClassState = useMemo(() => {
    return getJoinedClassState(myPassesData?.getMyPurchasedPasses?.data);
  }, [myPassesData]);

  const hasActivePass = useMemo(() => {
    const purchases = myPassesData?.getMyPurchasedPasses?.data;
    if (!Array.isArray(purchases)) return false;
    return purchases.some((purchase) => purchase?.status === "ACTIVE");
  }, [myPassesData]);

  const handleBookAction = async (classItem: any) => {
    const classId = classItem?.originalId || classItem?.id;
    const sessionDate = classItem?.singleDay || classItem?.day;
    if (!classId || !sessionDate || !hasActivePass) {
      setSnackbarMessage("Please purchase a pass to book a class.");
      setSnackbarType("info");
      setSnackbarOpen(true);
      schedulePassesRedirect({
        bookingContext: {
          classId,
          sessionDate,
          className: classItem?.className,
        },
      });
      return;
    }

    const { data: response } = await bookClassWithPass({
      variables: { classId, sessionDate },
    });

    if (response?.bookClassWithPass?.success) {
      setSnackbarMessage(response.bookClassWithPass.message || "Class booked successfully!");
      setSnackbarType("success");
      setSnackbarOpen(true);
      setSelectedClassToBook(null);
      await Promise.all([refetch(), refetchMyPasses()]);
      return;
    }
    const failMessage = response?.bookClassWithPass?.message || "Unable to book this class.";
    setSnackbarMessage(failMessage);
    setSnackbarType("error");
    setSnackbarOpen(true);
    if (/credit|pass/i.test(failMessage)) {
      schedulePassesRedirect({
        bookingContext: {
          classId,
          sessionDate,
          className: classItem?.className,
        },
      });
    }
  };

  const classes = useMemo(() => {
    const rawClasses = data?.getClassList?.data || [];
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const expanded = rawClasses.flatMap((cls: any) => {
      let dates: string[] = [];
      if (Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0) {
        dates = cls.scheduledDates;
      } else if (typeof cls.day === "string" && cls.day) {
        dates = [cls.day];
      } else if (Array.isArray(cls.day) && cls.day.length > 0) {
        dates = cls.day;
      }
      if (dates.length === 0) return [cls];
      return dates.map((date: string) => ({
        ...cls,
        id: `${cls.id}-${date}`,
        originalId: cls.id,
        singleDay: date
      }));
    });
    
    const filtered = expanded.filter((cls: any) => {
      const d = cls.singleDay;
      if (!d) return false;
      
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
      const parseTime = (t: string) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
      };
      const endTimeMinutes = parseTime(cls.endTime);
      const isEndedToday = d === todayStr && currentTimeMinutes >= endTimeMinutes;

      if (activeTab === "current") return d === todayStr && !isEndedToday;
      if (activeTab === "upcoming") return d > todayStr;
      if (activeTab === "previous") return d < todayStr || isEndedToday;
      return true;
    });

    filtered.sort((a: any, b: any) => {
      if (!a.singleDay || !b.singleDay) return 0;
      if (activeTab === "previous") {
        return new Date(b.singleDay).getTime() - new Date(a.singleDay).getTime();
      }
      return new Date(a.singleDay).getTime() - new Date(b.singleDay).getTime();
    });
    
    return filtered;
  }, [data?.getClassList?.data, activeTab]);

  useEffect(() => {
    if (!initialOpenClass || hasAutoOpenedClass || loading || classes.length === 0) return;

    const matchedClass = classes.find((cls: any) => {
      const targetId = cls.originalId || cls.id;
      const sameClass = targetId === initialOpenClass.classId;
      const sameSession =
        !initialOpenClass.sessionDate || cls.singleDay === initialOpenClass.sessionDate;
      return sameClass && sameSession;
    });

    if (matchedClass) {
      setSelectedClassToBook(matchedClass);
      setHasAutoOpenedClass(true);
      return;
    }

    const fallbackClass = classes.find(
      (cls: any) => (cls.originalId || cls.id) === initialOpenClass.classId,
    );

    if (fallbackClass) {
      setSelectedClassToBook(fallbackClass);
      setHasAutoOpenedClass(true);
    }
  }, [classes, hasAutoOpenedClass, initialOpenClass, loading]);


  const handleTabChange = (tab: "current" | "upcoming" | "previous") => {
    setActiveTab(tab);
    setPage(0);
  };

  const renderTabs = () => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 4 },
          px: { xs: 1, sm: 3 },
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {[
          { id: "current", label: "Today's Classes" },
          { id: "upcoming", label: "Upcoming Classes" },
          { id: "previous", label: "Previous Classes" },
        ].map((tab) => (
          <Typography
            key={tab.id}
            onClick={() => handleTabChange(tab.id as any)}
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 600,
              color: activeTab === tab.id ? themestyle.colors.activeGreen : "#6B7280",
              cursor: "pointer",
              pb: 1.5,
              borderBottom: activeTab === tab.id ? `3px solid ${themestyle.colors.activeGreen}` : "3px solid transparent",
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              "&:hover": { color: themestyle.colors.activeGreen },
            }}
          >
            {tab.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, value: number) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <ClassToolBar
        searchQuery={searchQuery}
        onSearchQueryChange={(query) => { setSearchQuery(query); setPage(0); }}
        instructorValue={selectedInstructor}
        dayValue={selectedDay}
        classTypeValue={selectedClassType}
        onInstructorChange={(val) => { setSelectedInstructor(val); setPage(0); }}
        onDayChange={(val) => { setSelectedDay(val); setPage(0); }}
        onClassTypeChange={(val) => { setSelectedClassType(val); setPage(0); }}
        classOptions={classOptions}
        classOptionsLoading={classOptionsLoading}
        instructorOptions={instructorOptions}
        hideBorder={true}
      />

      {renderTabs()}

      {loading ? (
        <CenteredLoader />
      ) : classes.length === 0 ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <Typography 
            variant="h6" 
            color="textSecondary"
            sx={{ fontSize: { xs: "13px", sm: "1rem" } }}
          >
            No classes found for this category.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {classes.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((cls: any) => {
            const classTypeConfig = CLASS_TYPE_UI[cls.classType] || {
              label: cls.classType,
              bg: "#F1F8EE",
              color: "#6CBC44",
            };
            const classDate = new Date((cls.singleDay || cls.day) + "T00:00:00");
            const dayName = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(classDate);
            const monthName = new Intl.DateTimeFormat("en-US", { month: "short" }).format(classDate);
            const dateNum = classDate.getDate();
            const formattedDateString = `${dayName} ${dateNum} ${monthName}`;
            const timeRange = `${formatTo12Hour(cls.startTime)} - ${formatTo12Hour(cls.endTime)}`;

              const classId = cls.originalId || cls.id;
              const sessionKey = `${classId}|${cls.singleDay || cls.day || ""}`;
              const isJoined = activeTab !== "previous" && joinedClassState.sessionKeys.has(sessionKey);

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cls.id} sx={{ display: "flex" }}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.08)",
                      border: "none",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      transition: "transform 0.3s ease, boxShadow 0.3s ease",
                      cursor: activeTab !== "previous" || isJoined ? "pointer" : "default",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                      "&:hover": {
                        transform: activeTab !== "previous" || isJoined ? "translateY(-8px)" : "none",
                        boxShadow: activeTab !== "previous" || isJoined ? "0px 15px 40px rgba(0, 0, 0, 0.12)" : "0px 10px 30px rgba(0, 0, 0, 0.08)",
                      },
                      "& .MuiCardContent-root": {
                        padding: "24px",
                      },
                    }}
                    onClick={() => {
                      const now = new Date();
                      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
                      const parseTime = (t: string) => {
                        const [h, m] = t.split(":").map(Number);
                        return h * 60 + m;
                      };
                      const startTimeMinutes = parseTime(cls.startTime);
                      const isToday = activeTab === "current";
                      const isStarted = isToday && currentTimeMinutes >= startTimeMinutes;

                      if (isJoined || (activeTab !== "previous" && !isStarted)) {
                        setSelectedClassToBook(cls);
                      }
                    }}
                  >
                    <Box sx={{ position: "relative", height: "240px", overflow: "hidden" }}>
                      {cls.coverImage ? (
                        <Box
                          component="img"
                          src={
                            cls.coverImage.startsWith("data:")
                              ? cls.coverImage
                              : cls.coverImage.startsWith("http")
                              ? cls.coverImage
                              : `data:image/*;base64,${cls.coverImage}`
                          }
                          alt={cls.className}
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "60%",
                          background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                        }}
                      />

                      {/* Class type badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: 16,
                          backgroundColor: "rgba(255, 255, 255, 0.85)",
                          backdropFilter: "blur(4px)",
                          color: classTypeConfig.color,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "12px",
                          fontWeight: 700,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        {classTypeConfig.label}
                      </Box>

                      {/* Joined badge */}
                      {isJoined && (
                        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
                          <Chip
                            icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important", color: "#2E7D32 !important" }} />}
                            label="Joined"
                            size="small"
                            sx={{
                              backgroundColor: "#E8F5E9",
                              color: "#2E7D32",
                              fontWeight: 700,
                              fontSize: "11px",
                              height: "24px",
                            }}
                          />
                        </Box>
                      )}
                    <Box sx={{ position: "absolute", bottom: 16, left: 16, color: "#fff" }}>
                        <TruncatedTitle
                          text={cls.className}
                          variant="h5"
                          sx={{
                            fontSize: "1.8rem",
                            mb: 0.5,
                            lineHeight: 1.1,
                            fontWeight: 800,
                          }}
                        />
                      <Typography
                        variant="body1"
                        sx={{ opacity: 0.9, fontWeight: 500, fontSize: "1rem" }}
                      >
                        Instructor: {cls.instructorName}
                      </Typography>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                      <Box
                        sx={{
                          color: themestyle.colors.activeGreen,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#1A202C",
                          fontWeight: 700,
                          fontSize: "1rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {formattedDateString} {timeRange}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      {(() => {
                        const now = new Date();
                        const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
                        const parseTime = (t: string) => {
                          const [h, m] = t.split(":").map(Number);
                          return h * 60 + m;
                        };
                        const startTimeMinutes = parseTime(cls.startTime);
                        const endTimeMinutes = parseTime(cls.endTime);
                        const isToday = activeTab === "current";
                        const isStarted = isToday && currentTimeMinutes >= startTimeMinutes;
                        const isEnded = isToday && currentTimeMinutes >= endTimeMinutes;

                        const isDisabled = (activeTab === "previous" || isStarted || isEnded) && !isJoined;
                        const buttonText = isJoined ? "Joined" : isEnded || activeTab === "previous" ? "Closed" : isStarted ? "Ongoing" : "Book Now";
                        const buttonColor = isDisabled ? "#FFEBEE" : isJoined ? "#E8F5E9" : "#5AA839";
                        const textColor = isDisabled ? "#D32F2F" : isJoined ? "#2E7D32" : "#fff";

                        return (
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={isDisabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeTab !== "previous" && !isStarted && !isEnded) {
                                setSelectedClassToBook(cls);
                              }
                            }}
                            sx={{
                              backgroundColor: buttonColor,
                              color: textColor,
                              borderRadius: "10px",
                              textTransform: "none",
                              fontSize: "14px",
                              py: 1,
                              fontWeight: 600,
                              boxShadow: "none",
                              cursor: isDisabled ? "default" : "pointer",
                              pointerEvents: "auto",
                              "&:hover": {
                                backgroundColor: isDisabled ? buttonColor : isJoined ? "#E8F5E9" : "#4A8F2A",
                                boxShadow: isDisabled || isJoined
                                  ? "none"
                                  : "0px 4px 12px rgba(90, 168, 57, 0.2)",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: buttonColor,
                                color: textColor,
                              },
                            }}
                          >
                            {buttonText}
                          </Button>
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {!loading && classes.length > (isMobile ? 3 : 9) && (
        <Box sx={{ mt: 4, mb: 2 }}>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                py: 2,
                px: 2,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #F3F4F6",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: "14px",
                  color: "#1F2937",
                  fontWeight: 500,
                  mr: 2,
                }}
              >
                {page * rowsPerPage + 1} -{" "}
                {Math.min((page + 1) * rowsPerPage, classes.length)}
                <span
                  style={{
                    marginLeft: "8px",
                    marginRight: "8px",
                    color: "#9CA3AF",
                  }}
                >
                  of
                </span>
                {classes.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": {
                      color: "#D1D5DB",
                      borderColor: "#F3F4F6",
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 12L6 8L10 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>

                <IconButton
                  size="small"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * rowsPerPage >= classes.length}
                  sx={{
                    width: "32px",
                    height: "32px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    color: "#1F2937",
                    "&:disabled": {
                      color: "#D1D5DB",
                      borderColor: "#F3F4F6",
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M6 4L10 8L6 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </IconButton>
              </Box>
            </Box>
          ) : (
            <TablePagination
              component="div"
              count={classes.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[9, 18, 27]}
              ActionsComponent={CustomPaginationActions}
              sx={{
                borderTop: "1px solid #E5E7EB",
                "& .MuiTablePagination-root": {
                  alignItems: "center",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: "13px",
                    color: "#6B7280",
                  },
                "& .MuiTablePagination-displayedRows": {
                  marginRight: "16px",
                },
                "& .MuiTablePagination-actions": {
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  ml: 2,
                },
              }}
            />
          )}
        </Box>
      )}
      <ClassPurchaseDrawer
        open={!!selectedClassToBook}
        onClose={() => setSelectedClassToBook(null)}
        classData={selectedClassToBook}
        requiresPass={!hasActivePass}
        isJoined={
          selectedClassToBook
            ? activeTab !== "previous" && joinedClassState.sessionKeys.has(
                `${selectedClassToBook.originalId || selectedClassToBook.id}|${selectedClassToBook.singleDay || selectedClassToBook.day || ""}`,
              ) ||
              false
            : false
        }
        onBookAction={() => {
          if (selectedClassToBook) {
            void handleBookAction(selectedClassToBook);
          }
        }}
      />
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};
export default UserClassesCardView;
